import "./legacy-headlines.css";

import SearchBar from "@/ui-components/search-bar";
import { useDeleteHeadline } from "../api";
import { useSearchHeadlines } from "@/shared/api/newsquawk-headlines";
import LegacyNewsquawkHeadlineItem from "./LegacyNewsquawkHeadlineItem";
import { groupHeadlinesByDate } from "../api/utils";
import React, { useEffect, useMemo, useState } from "react";
import { GroupedVirtuoso } from "react-virtuoso";
import toast from "react-hot-toast";

import { Headline } from "../models/Headline";
import EditHeadlineFormDialog from "../EditHeadlineFormDialog";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { LoadMoreButton } from "@/ui-components/load-more-button";

import * as Sentry from "@sentry/react";
import { trackSearchSubmit, trackSearchResults } from "@/utils/gtm";
import { useWidgetSearchState } from "@/hooks/useWidgetSearchState";
import type { CommonSearchProps } from "../HeadlinesSearchForm";

/**
 * Legacy-skinned search form for the /headlines page. Identical behaviour to the
 * v2 HeadlinesSearchForm, but its result rows render as legacy headline items.
 */
function LegacyHeadlinesSearchForm(
  props: CommonSearchProps & { tagIds?: string[]; useLocalStore?: boolean }
) {
  const {
    isLoading,
    isSuccess,
    isError,
    searchHeadlines,
    searchResults,
    clearSearchResults,
    loadNext,
    hasMore,
    isLoadingMore,
  } = useSearchHeadlines();

  const { groups, groupedCounts } = useMemo(
    () => groupHeadlinesByDate(searchResults),
    [searchResults]
  );

  const [editingHeadline, setEditingHeadline] = useState<Headline | null>(null);
  const [deletingHeadline, setDeletingHeadline] = useState<Headline | null>(
    null
  );

  const { deleteHeadlineById } = useDeleteHeadline();

  const { setTitle, formOpen = true, persistKey, onActiveChange } = props;
  const [savedSearch, setSavedSearch] = useWidgetSearchState(persistKey);

  const [exactMatch, setExactMatch] = React.useState(
    savedSearch.exactMatch ?? false
  );

  const [startDate, setStartDate] = React.useState<string>(
    savedSearch.startDate ?? ""
  );
  const [startTime, setStartTime] = React.useState<string>(
    savedSearch.startTime ?? "00:00"
  );
  const [endDate, setEndDate] = React.useState<string>(
    savedSearch.endDate ?? ""
  );
  const [endTime, setEndTime] = React.useState<string>(
    savedSearch.endTime ?? "23:59"
  );

  const [query, setQuery] = React.useState<string>(savedSearch.query ?? "");

  const showSearchResults = searchResults.length > 0 || isSuccess;

  const formRef = React.useRef<HTMLDivElement>(null);
  const [formHeight, setFormHeight] = React.useState<number>(70);

  const runSearch = (q: string) => {
    if (!q.trim() && !startDate && !endDate) return;

    let startDateISO: string | undefined;
    let endDateISO: string | undefined;

    if (startDate) {
      const [h, m] = (startTime || "00:00").split(":").map(Number);
      const startDateObj = new Date(startDate);
      startDateObj.setHours(h, m, 0, 0);
      startDateISO = startDateObj.toISOString();
    }

    if (endDate) {
      const [h, m] = (endTime || "23:59").split(":").map(Number);
      const endDateObj = new Date(endDate);
      endDateObj.setHours(h, m, 59, 999);
      endDateISO = endDateObj.toISOString();
    }

    searchHeadlines({
      query: q,
      subscribed: true,
      exactMatch: exactMatch,
      rows: 30,
      dateRange: {
        startDate: startDateISO,
        endDate: endDateISO,
      },
    });
  };

  const handleSearch = (q: string) => {
    if (!q.trim() && !startDate && !endDate) return;

    runSearch(q);

    setSavedSearch({
      query: q,
      startDate,
      startTime,
      endDate,
      endTime,
      exactMatch,
    });

    const activeFilters: string[] = [];
    if (exactMatch) activeFilters.push("exact_match");
    if (startDate || endDate) activeFilters.push("date_range");
    if (props.tagIds?.length) activeFilters.push("tag_filter");
    trackSearchSubmit({
      search_term: q,
      search_context: "headlines",
      search_filters:
        activeFilters.length > 0 ? activeFilters.join(", ") : undefined,
    });
  };

  const handleClear = () => {
    setQuery("");
    setStartDate("");
    setEndDate("");
    clearSearchResults();
    setSavedSearch({});
  };

  let footer = <Empty />;

  if (isSuccess && searchResults.length === 0) {
    footer = <Footer>No results found</Footer>;
  } else if (hasMore) {
    footer = <LoadMoreButton loading={isLoadingMore} loadNextPage={loadNext} />;
  } else if (searchResults.length > 0) {
    footer = <Footer>End of search results</Footer>;
  }

  useEffect(() => {
    if (!isError) return;
    toast.error("There was an error fetching the headlines, please try again.");
    Sentry.captureMessage("Error fetching headlines in search");
  }, [isError]);

  useEffect(() => {
    if (isSuccess && query) {
      const activeFilters: string[] = [];
      if (exactMatch) activeFilters.push("exact_match");
      if (startDate || endDate) activeFilters.push("date_range");
      trackSearchResults({
        search_term: query,
        search_context: "headlines",
        results_count: searchResults.length,
        search_filters:
          activeFilters.length > 0 ? activeFilters.join(", ") : undefined,
      });
    }
  }, [isSuccess, query, searchResults.length, exactMatch, startDate, endDate]);

  useEffect(() => {
    const titleString = query
      ? `Showing results for '${query}'`
      : "Showing search results";

    if (showSearchResults) {
      setTitle?.(titleString);
    } else {
      setTitle?.("Newsquawk Headlines");
    }

    return () => setTitle?.("Newsquawk Headlines");
  }, [showSearchResults, query, setTitle]);

  // Re-apply a persisted search on mount (after Done / tab switch / reload).
  useEffect(() => {
    if (
      savedSearch.query?.trim() ||
      savedSearch.startDate ||
      savedSearch.endDate
    ) {
      runSearch(savedSearch.query ?? "");
    }
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onActiveChange?.(showSearchResults);
  }, [showSearchResults, onActiveChange]);

  useEffect(() => clearSearchResults, [clearSearchResults]);

  useEffect(() => {
    const updateFormHeight = () => {
      if (formRef.current) {
        setFormHeight(formRef.current.offsetHeight);
      }
    };

    updateFormHeight();

    const resizeObserver = new ResizeObserver(updateFormHeight);
    if (formRef.current) {
      resizeObserver.observe(formRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <React.Fragment>
      <div
        className={`bg-gray-800 border-b border-gray-700/50 px-3 py-2 space-y-2${
          formOpen ? "" : " hidden"
        }`}
        ref={formRef}
      >
        <SearchBar
          value={query}
          onChange={setQuery}
          loading={isLoading}
          onSearch={handleSearch}
          onClear={handleClear}
          showSubmitButton={false}
        />

        <div className="sticky bottom-0 z-10 bg-gray-800 flex flex-wrap items-center gap-x-4 gap-y-2">
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={exactMatch}
              onChange={(e) => setExactMatch(e.target.checked)}
              className="size-3.5 rounded border-gray-600 bg-gray-900 text-squawk-blue focus:ring-0 focus:ring-offset-0"
            />
            <span className="text-xs text-gray-400">Exact match</span>
          </label>

          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">From:</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">To:</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            {(query || startDate || endDate || showSearchResults) && (
              <button
                type="button"
                onClick={handleClear}
                className="px-3 py-1 text-xs font-medium rounded text-gray-300 hover:bg-gray-700 transition-colors"
              >
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={() => handleSearch(query)}
              disabled={!query.trim() && !startDate && !endDate}
              className="px-3 py-1 text-xs font-medium rounded bg-gray-700 text-gray-200 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Search
            </button>
            {props.onClose && (
              <button
                onClick={props.onClose}
                className="px-3 py-1 text-xs font-medium rounded bg-squawk-blue text-white hover:bg-squawk-blue/80 transition-colors"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>

      {showSearchResults && (
        <section
          className="legacy-headlines absolute z-30 bg-white dark:bg-gray-900 inset-0 pb-[40px] overflow-y-auto scrollbar-thin"
          style={{ top: formOpen ? `${formHeight}px` : 0 }}
        >
          <GroupedVirtuoso
            className="scrollbar-thin inset-x-0 top-0"
            groupCounts={groupedCounts}
            groupContent={(index) =>
              groups[index] == "Today" ? (
                <p className="h-[1px]" />
              ) : (
                <div className="lh-day">{groups[index]}</div>
              )
            }
            itemContent={(idx) => (
              <LegacyNewsquawkHeadlineItem
                key={searchResults[idx].id}
                headline={searchResults[idx]}
                onItemDelete={setDeletingHeadline}
                onItemEdit={setEditingHeadline}
                searchQuery={query}
              />
            )}
            components={{
              Footer: () => footer,
            }}
          />
        </section>
      )}

      {/* Dialogs */}
      <section>
        <EditHeadlineFormDialog
          isOpen={!!editingHeadline}
          headline={editingHeadline!}
          onClose={() => setEditingHeadline(null)}
        />

        <DeleteConfirmationDialog
          isOpen={!!deletingHeadline}
          onClose={() => setDeletingHeadline(null)}
          onConfirmDelete={() =>
            deletingHeadline
              ? deleteHeadlineById(deletingHeadline.id)
              : Promise.reject()
          }
        />
      </section>
    </React.Fragment>
  );
}

const Empty = () => null;

const Footer = ({ children }: { children: React.ReactNode }) => (
  <p className="font-medium text-sm text-center text-squawk-blue-light py-1">
    {children}
  </p>
);

export default LegacyHeadlinesSearchForm;
